export type Result<T, E> = Ok<T> | Err<E>;

export type Ok<T> = {
  readonly success: true;
  readonly data: T;
};

export type Err<E> = {
  readonly success: false;
  readonly error: E;
};

export const ok = <T>(data: T): Ok<T> => ({ success: true, data });
export const err = <E>(error: E): Err<E> => ({ success: false, error });

export const isOk = <T, E>(result: Result<T, E>): result is Ok<T> => result.success;
export const isErr = <T, E>(result: Result<T, E>): result is Err<E> => !result.success;

export const map = <T, U, E>(result: Result<T, E>, fn: (value: T) => U): Result<U, E> => {
  return isOk(result) ? ok(fn(result.data)) : result;
};

export const mapErr = <T, E, F>(result: Result<T, E>, fn: (error: E) => F): Result<T, F> => {
  return isErr(result) ? err(fn(result.error)) : result;
};

export const andThen = <T, U, E>(
  result: Result<T, E>,
  fn: (value: T) => Result<U, E> | Promise<Result<U, E>>
): Result<U, E> | Promise<Result<U, E>> => {
  return isOk(result) ? fn(result.data) : result;
};

export const orElse = <T, E, F>(
  result: Result<T, E>,
  fn: (error: E) => Result<T, F> | Promise<Result<T, F>>
): Result<T, F> | Promise<Result<T, F>> => {
  return isErr(result) ? fn(result.error) : result;
};

export const unwrapOr = <T, E>(result: Result<T, E>, defaultValue: T): T => {
  return isOk(result) ? result.data : defaultValue;
};
